// ABOUTME: Labelled input of the auth screens — 44px control, required mark, password reveal, messages.
// ABOUTME: Own component rather than CTextInput: that one is a 36px in-app field with its own chrome.

import { useState, type InputHTMLAttributes, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { EyeIcon, EyeOffIcon } from "@shared/icons"
import { cn } from "@shared/lib/utils"

interface AuthFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string
  type?: string
  /** Field-level messages from Kratos, rendered under the control. */
  messages?: ReactNode
  /** Draws the control in the error state (red border). */
  invalid?: boolean
  /** 36px form height instead of the 44px standalone-screen one (docs/DESIGN_SYSTEM.md §1). */
  compact?: boolean
}

export const AuthField = ({
  label,
  type = "text",
  required,
  messages,
  invalid,
  compact,
  className,
  ...rest
}: AuthFieldProps) => {
  const { t } = useTranslation()
  const [revealed, setRevealed] = useState(false)
  const isPassword = type === "password"

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-secondary-foreground">
        {label} {required && <span className="text-danger-strong">*</span>}
      </label>
      <div className="relative flex items-center">
        <input
          type={isPassword && revealed ? "text" : type}
          required={required}
          aria-invalid={invalid}
          className={cn(
            // Text colour comes from the global `input` rule in index.css.
            "w-full rounded-lg border border-gray-300 bg-white px-3 shadow-inputShadow transition-colors",
            compact ? "h-9 text-sm" : "text-control h-11",
            "placeholder:text-gray-400 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none",
            // Room for the reveal button so long passwords don't run under it.
            isPassword && "pr-11",
            invalid && "border-red-500",
            className,
          )}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            // Icon-only control: name the action it performs for screen readers.
            aria-label={revealed ? t("auth.hidePassword") : t("auth.showPassword")}
            aria-pressed={revealed}
            onClick={() => setRevealed((v) => !v)}
            className={cn(
              "absolute right-1.5 rounded-md leading-none text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600",
              compact ? "p-1" : "p-1.5",
            )}
          >
            {revealed ? <EyeOffIcon size={compact ? 16 : 18} /> : <EyeIcon size={compact ? 16 : 18} />}
          </button>
        )}
      </div>
      {messages}
    </div>
  )
}
